import { selectTrajectoryWaypointPanoramas } from "@/components/r3f/renderers/odometry-paths/path-selectors";
import { useOdometryPathWorldPositions } from "@/components/r3f/renderers/odometry-paths/use-odometry-path-positions";
import { useWalkPlaceholderPositions } from "@/hooks/use-walk-placeholder-positions";
import { GaussianSplatsObject } from "@/object-cache";
import { Features, selectHasFeature } from "@/store/features/features-slice";
import { selectModeName } from "@/store/mode-selectors";
import { useAppSelector } from "@/store/store-hooks";
import { selectGuidedSplatsNavigation } from "@/store/view-options/view-options-selectors";
import { SceneFilter } from "@/types/scene-filter";
import {
  isIElementOdometryPath,
  type IElementGenericOdometryPath,
} from "@faro-lotv/ielement-types";
import { UPDATE_CAMERA_MONITOR_PRIORITY } from "@faro-lotv/spatial-ui";
import { useFrame } from "@react-three/fiber";
import { isEqual } from "es-toolkit";
import { JSX, useEffect, useRef, useState } from "react";
import { Raycaster, Vector3 } from "three";
import { ImageType } from "../../data-dispatch/flywalk-data-dispatch";
import { useToolsAcquaintance } from "../tools-acquaintance-context";
import { SplatHit } from "./guided-navigation-math";
import { GUIDED_NAVIGATION_PARAMS as P } from "./guided-navigation-params";
import { GuidedNavigationState, nowSeconds } from "./guided-navigation-state";

/** Cosine of the movement direction change after which the raycast on the splats is repeated right away */
const DIRECTION_CHANGE_COS = Math.cos(Math.PI / 12);

type GuidedSplatsNavigationToolProps = {
  /** The current scene filter */
  sceneFilter: SceneFilter;
  /** The rendered Gaussian Splats, if any */
  gaussianSplats: GaussianSplatsObject | null;
  /** The current image type */
  imageType: ImageType;
  /** Whether a flywalk animation is running */
  isAnimating: boolean;
  /** The paths whose trajectories define the navigable tunnels */
  paths: IElementGenericOdometryPath[];
};

/** @returns the tool constraining the camera navigation through Gaussian Splats around the scan trajectories */
export function GuidedSplatsNavigationTool({
  sceneFilter,
  gaussianSplats,
  imageType,
  isAnimating,
  paths,
}: GuidedSplatsNavigationToolProps): JSX.Element | null {
  const { guidedNavigation, setGuidedNavigationActive } =
    useToolsAcquaintance();

  const hasFeature = useAppSelector(
    selectHasFeature(Features.GuidedSplatsNavigation),
  );
  const isEnabled = useAppSelector(selectGuidedSplatsNavigation);
  const modeName = useAppSelector(selectModeName);

  const isActive =
    hasFeature &&
    isEnabled &&
    (modeName === "walk" || modeName === "split") &&
    sceneFilter === SceneFilter.GaussianSplats &&
    !!gaussianSplats &&
    imageType === ImageType.NoImage;

  useEffect(() => {
    setGuidedNavigationActive(isActive);
    return () => setGuidedNavigationActive(false);
  }, [isActive, setGuidedNavigationActive]);

  useSplatProximityRaycast(
    isActive && !isAnimating ? gaussianSplats : null,
    guidedNavigation,
  );

  if (!isActive) return null;

  return (
    <>
      {paths.map((path) => (
        <TrajectorySource key={path.id} path={path} state={guidedNavigation} />
      ))}
    </>
  );
}

type TrajectorySourceProps = {
  path: IElementGenericOdometryPath;
  state: GuidedNavigationState;
};

/** @returns null, after feeding the trajectory of a path, at the acquisition device positions, to the guided navigation state */
function TrajectorySource({ path, state }: TrajectorySourceProps): null {
  // A path either references an odometry file, or groups the panoramas it was reconstructed from
  const odometryPath = isIElementOdometryPath(path) ? path : undefined;
  const panos = useAppSelector(
    selectTrajectoryWaypointPanoramas(path),
    isEqual,
  );
  const panoPoints = useWalkPlaceholderPositions(panos, undefined, true);
  const filePoints = useOdometryPathWorldPositions(odometryPath);
  const points = odometryPath ? filePoints : panoPoints;

  useEffect(() => {
    state.setTrajectory(path.id, points);
    return () => state.setTrajectory(path.id, undefined);
  }, [state, path.id, points]);

  return null;
}

/**
 * While the camera moves, raycasts the splats along its horizontal movement direction to find the closest wall.
 * While it moves vertically, it also raycasts them up or down to find the closest ceiling or floor.
 * At most one raycast is done per frame, as each one scans all the splats.
 *
 * @param splats the splats to raycast, null to disable the raycast
 * @param state the guided navigation state receiving the raycast result
 */
function useSplatProximityRaycast(
  splats: GaussianSplatsObject | null,
  state: GuidedNavigationState,
): void {
  const [raycaster] = useState(() => new Raycaster());
  const [hits] = useState<Parameters<GaussianSplatsObject["raycast"]>[1]>(
    () => [],
  );
  const lastRaycastTime = useRef(-Infinity);
  const lastRaycastDirection = useRef(new Vector3());
  const lastVerticalRaycastTime = useRef(-Infinity);
  const lastVerticalDirection = useRef(0);

  useFrame(({ camera }) => {
    const { splatHit, verticalSplatHit, verticalDirection, moveDirection } =
      state;
    const now = nowSeconds();
    if (!splats || !state.isMoving(now)) {
      splatHit.valid = false;
      verticalSplatHit.valid = false;
      return;
    }
    const isMovingHorizontally = moveDirection.lengthSq() > 0;
    if (!isMovingHorizontally) splatHit.valid = false;
    if (verticalDirection === 0) verticalSplatHit.valid = false;
    // The previous hits are useless when changing direction, so cast again right away
    if (
      moveDirection.dot(lastRaycastDirection.current) < DIRECTION_CHANGE_COS
    ) {
      lastRaycastDirection.current.copy(moveDirection);
      lastRaycastTime.current = -Infinity;
    }
    if (verticalDirection !== lastVerticalDirection.current) {
      lastVerticalDirection.current = verticalDirection;
      lastVerticalRaycastTime.current = -Infinity;
    }

    raycaster.camera = camera;
    raycaster.ray.origin.setFromMatrixPosition(camera.matrixWorld);
    if (
      isMovingHorizontally &&
      now - lastRaycastTime.current >= P.raycastInterval
    ) {
      lastRaycastTime.current = now;
      raycaster.ray.direction.copy(moveDirection);
      raycastClosest(splats, raycaster, hits, splatHit);
    } else if (
      verticalDirection !== 0 &&
      now - lastVerticalRaycastTime.current >= P.raycastInterval
    ) {
      lastVerticalRaycastTime.current = now;
      raycaster.ray.direction.set(0, verticalDirection, 0);
      raycastClosest(splats, raycaster, hits, verticalSplatHit);
    }
  }, UPDATE_CAMERA_MONITOR_PRIORITY);
}

/**
 * Raycasts the splats and stores the closest hit
 *
 * @param splats the splats to raycast
 * @param raycaster the raycaster with the ray already set
 * @param hits scratch array receiving the raw hits
 * @param out receives the closest hit
 */
function raycastClosest(
  splats: GaussianSplatsObject,
  raycaster: Raycaster,
  hits: Parameters<GaussianSplatsObject["raycast"]>[1],
  out: SplatHit,
): void {
  raycaster.near = 0;
  raycaster.far = P.raycastRange;
  hits.length = 0;
  splats.raycast(raycaster, hits);

  let closest = Infinity;
  for (const h of hits) closest = Math.min(closest, h.distance);
  out.valid = Number.isFinite(closest);
  if (out.valid) {
    out.distance = closest;
    out.origin.copy(raycaster.ray.origin);
    out.direction.copy(raycaster.ray.direction);
  }
}
